/* eslint-disable no-restricted-syntax */
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AssociationType } from '../../types';
import {
  closestCorners,
  CollisionDetection,
  DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, pointerWithin, useSensor, useSensors
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { DroppableList } from '../editExpectedFromModal/components/DroppableList';
import { AssociationDroppableItem } from '../editExpectedFromModal/components/AssociationDroppableItem';
import { orderAssociationsAlphabetically } from '../../utils';
import { TemplateFileData } from 'graphql/documents/mutations';
import { PartyInvolvedDocuments } from 'v2-types/order';

export const useStyles = makeStyles((theme: any) => ({
  container: { padding: theme.spacing(2) },
  title: {
    fontWeight: 500,
    padding: theme.spacing(2),
    color: theme.palette.primary.main,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
    paddingBottom: theme.spacing(2)
  },
  content: {
    padding: theme.spacing(2)
  },
  subtitle: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    paddingBottom: "0.3rem"
  },
  titleList: {
    fontWeight: 500,
    fontSize: "0.9rem",
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    textAlign: "center",
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
  },
  divider: { backgroundColor: theme.accent.main },
  dropzoneContainer: {
    borderWidth: 2,
    borderRadius: 2,
    borderColor: 'lightgray',
    borderStyle: 'dashed',
    padding: theme.spacing(5),
    width: '90%'
  },
  fileContainer: {
    borderRadius: '15px',
    background: '#ececec',
    width: 'auto',
    paddingRight: theme.spacing(2)
  },
  removeFileContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#646b6e',
  },
  actionButtons: { padding: theme.spacing(2) },
  errorIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
}));

type Props = {
	onClose: () => void,
	isSavingLoading: boolean,
	parties: AssociationType[],
	properties: AssociationType[],
	documentData: PartyInvolvedDocuments[]
	onSave: (data: TemplateFileData[]) => void
}

const allPartiesContainerId = "allParties";
const availablePropertiesContainerId = "availableProperties";
const documentPropertiesContainerId = "documentProperties";
const deliveryPartiesContainerId = "deliveryParties";
const documentPartiesContainerId = "documentParties";
const signerPartiesContainerId = "signerParties";

const containerIdList = [
  allPartiesContainerId,
  availablePropertiesContainerId,
  documentPropertiesContainerId,
  deliveryPartiesContainerId,
  documentPartiesContainerId,
  signerPartiesContainerId,
];

const partiesContainerIds = [
  allPartiesContainerId,
  deliveryPartiesContainerId,
  documentPartiesContainerId,
  signerPartiesContainerId,
];

const propertiesContainerIds = [
  availablePropertiesContainerId,
  documentPropertiesContainerId
];

const containerRegexId = /(allParties|deliveryParties|documentParties|signerParties|availableProperties|documentProperties)/gi;

const filterByKind = (data: PartyInvolvedDocuments[], kind: string) => data.filter(item => item.kind === kind);

const mapToParty = (containerIdSuffix: string) => ({ kinds, name, _id }: PartyInvolvedDocuments | AssociationType) => ({
  kinds: kinds ?? undefined,
  name,
  _id: _id + containerIdSuffix,
} as AssociationType);

const mapToProperty = (containerIdSuffix: string) => ({ kinds, _id, address }) => ({
  kinds: kinds ?? undefined,
  name: address!,
  _id: _id + containerIdSuffix
});

export const EditTemplateDataModal: React.FC<Props> = ({
  onClose,
  isSavingLoading,
  parties,
  properties,
  documentData,
  onSave
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [partiesCombinedState, setPartiesCombinedState] = useState<{
		allParties: AssociationType[],
		documentParties: AssociationType[],
		deliveryParties: AssociationType[],
		signerParties: AssociationType[],
	}>({
	  allParties: orderAssociationsAlphabetically(
	    parties
	      .filter(({ _id }) => !(
	        documentData.some((dataEntry) => dataEntry._id === _id && dataEntry.kind === "party")
					&& documentData.some((dataEntry) => dataEntry._id === _id && dataEntry.kind === "signer")
					&& documentData.some((dataEntry) => dataEntry._id === _id && dataEntry.kind === "deliver")
	      ))
	      .map(mapToParty(allPartiesContainerId))),
	  documentParties: filterByKind(documentData, "party").map(mapToParty(documentPartiesContainerId)),
	  signerParties: filterByKind(documentData, "signer").map(mapToParty(signerPartiesContainerId)),
	  deliveryParties: filterByKind(documentData, "deliver").map(mapToParty(deliveryPartiesContainerId)),
	});

  const [propertiesCombinedState, setPropertiesCombinedState] = useState<{
		availableProperties: AssociationType[],
		documentProperties: AssociationType[],
	}>({
	  availableProperties: orderAssociationsAlphabetically(
	    properties
	      .filter(({ _id }) => !documentData.some(({ _id: dataId }) => _id === dataId))
	      .map(party => ({
	        ...party,
	        _id: party._id + availablePropertiesContainerId
	      }))),
	  documentProperties: filterByKind(documentData, "property").map(mapToProperty(documentPropertiesContainerId))
	});
  const [activeAssociationId, setActiveAssociationId] = useState<string | null>("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const collisionDetectionStrategy: CollisionDetection = (args) => {
    const pointerIntersections = pointerWithin(args);

    if (pointerIntersections.length > 0) {
      const overContainer = pointerIntersections.find(
        ({ id }) => containerIdList.includes(id as string)
      );

      if (overContainer) {
        return [overContainer];
      }
    }

    return closestCorners(args);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;

    setActiveAssociationId(active.id as string);
  };

  const activeAssociation: AssociationType | null = activeAssociationId
    ? [
      ...partiesCombinedState.allParties,
      ...partiesCombinedState.deliveryParties,
      ...partiesCombinedState.documentParties,
      ...partiesCombinedState.signerParties,
      ...propertiesCombinedState.availableProperties,
      ...propertiesCombinedState.documentProperties
    ].find(({ _id }) => _id === activeAssociationId)!
    : null;

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    const activeContainerId = active.data.current?.containerId as string;

    setActiveAssociationId(null);

    if (!over || !activeContainerId) return;

    const overContainerId = over.id as string;

    const originalActiveId = active.id.toString().replace(containerRegexId, "");

    const originalActiveItem = partiesContainerIds.includes(activeContainerId)
      ? parties.find(({ _id }) => _id === originalActiveId)
      : properties.find(({ _id }) => _id === originalActiveId);

    if (!originalActiveItem) return;

    const association = {
      ...originalActiveItem,
      _id: originalActiveItem._id + overContainerId
    };

    if (partiesContainerIds.includes(activeContainerId) && partiesContainerIds.includes(overContainerId)) {
      setPartiesCombinedState(prevState => {
        const mutablePartyState = { ...prevState };

        if (!mutablePartyState[overContainerId].some(({ _id }) => _id === association._id)) {
          mutablePartyState[overContainerId] = orderAssociationsAlphabetically(
            [...mutablePartyState[overContainerId], association]
          );
        }

        if (activeContainerId !== allPartiesContainerId) {
          mutablePartyState[activeContainerId] = mutablePartyState[activeContainerId]
            .filter(({ _id }) => _id !== active.id.toString());
        }

        if (
          [
            allPartiesContainerId,
            deliveryPartiesContainerId,
            documentPartiesContainerId,
            signerPartiesContainerId
          ].every(
            (containerId) => mutablePartyState[containerId].some(
              ({ _id }) => _id.replace(containerRegexId, "") === originalActiveId
            )
          )
        ) {
          mutablePartyState.allParties = mutablePartyState.allParties.filter(({ _id }) => _id.replace(containerRegexId, "") !== originalActiveId);
        }

        return mutablePartyState;
      });
    } else if (propertiesContainerIds.includes(activeContainerId) && propertiesContainerIds.includes(overContainerId)) {
      setPropertiesCombinedState(prevState => {
        const mutablePropertyState = { ...prevState };

        if (!mutablePropertyState[overContainerId].some(({ _id }) => _id === association._id)) {
          mutablePropertyState[overContainerId] = orderAssociationsAlphabetically(
            [...mutablePropertyState[overContainerId], association]
          );

          mutablePropertyState[activeContainerId] = mutablePropertyState[activeContainerId]
            .filter(({ _id }) => _id !== active.id.toString());
        }
        return mutablePropertyState;
      });
    }
  }, [parties, properties]);

  const handleSave = () => {
    const documentProperties = propertiesCombinedState
      .documentProperties.map(({ _id }) => ({
        kind: "property",
        _id: _id.replace(containerRegexId, "")
      }));

    const documentParties = partiesCombinedState
      .documentParties.map(({ _id }) => ({
        kind: "party",
        _id: _id.replace(containerRegexId, "")
      }));

    const documentDelivery = partiesCombinedState
      .deliveryParties.map(({ _id }) => ({
        kind: "deliver",
        _id: _id.replace(containerRegexId, "")
      }));

    const documentSigners = partiesCombinedState
      .signerParties.map(({ _id }) => ({
        kind: "signer",
        _id: _id.replace(containerRegexId, "")
      }));

    onSave([
      ...documentProperties,
      ...documentParties,
      ...documentDelivery,
      ...documentSigners
    ] as TemplateFileData[]);
  };

  return (
    <Dialog open keepMounted={false} maxWidth="lg" fullWidth>
      <DialogTitle style={{ padding: "0px" }}>
        <Typography variant="h5" className={classes.title}>
          {t('documents:templateData')}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ padding: 0, maxWidth: "100%", overflowX: "hidden" }}>
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetectionStrategy}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box
            style={{
						  display: "grid",
						  gap: "1rem",
						  width: "100%",
						  height: isMobile ? "auto" : "100%",
						  minWidth: 0,
						  gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "1fr 1rem 3fr",
						  gridTemplateRows: isMobile ? "auto" : "12rem 1fr"
            }}
            className={classes.content}
            data-testid="template-data-layout"
          >
            <DroppableList
              associations={propertiesCombinedState.availableProperties}
              title={t('documents:available-properties')}
              droppableContainerId={availablePropertiesContainerId}
              height="12rem"
            />
            <Divider
              className={classes.divider}
              style={{
							  gridRow: isMobile ? "auto" : "span 3",
							  height: isMobile ? undefined : "100%",
							  justifySelf: "center",
							  width: isMobile ? "100%" : "0.1rem"
              }}
              orientation={isMobile ? "horizontal" : "vertical"}
            />
            <DroppableList
              associations={propertiesCombinedState.documentProperties}
              title={t('documents:properties-in-document')}
              droppableContainerId={documentPropertiesContainerId}
              height="12rem"
            />

            <DroppableList
              associations={partiesCombinedState.allParties}
              title={t('documents:available-parties')}
              droppableContainerId={allPartiesContainerId}
              height={isMobile ? "12rem" : undefined}
            />
            {/* <Divider /> */}
            <Box
              data-testid="party-destinations"
              style={{
							  display: "flex",
							  flexDirection: isMobile ? "column" : "row",
							  gap: "1rem",
							  width: "100%",
							  minWidth: 0
              }}
            >
              <DroppableList
                associations={partiesCombinedState.deliveryParties}
                title={t('documents:deliver-to')}
                droppableContainerId={deliveryPartiesContainerId}
                height={isMobile ? "12rem" : undefined}
              />
              <DroppableList
                associations={partiesCombinedState.documentParties}
                title={t('documents:parties-in-document')}
                droppableContainerId={documentPartiesContainerId}
                height={isMobile ? "12rem" : undefined}
              />
              <DroppableList
                associations={partiesCombinedState.signerParties}
                title={t('documents:document-signers')}
                droppableContainerId={signerPartiesContainerId}
                height={isMobile ? "12rem" : undefined}
              />
              <DragOverlay adjustScale={false} style={{ maxWidth: "500px", maxHeight: "100px" }}>
                {activeAssociation ? (
                  <AssociationDroppableItem association={activeAssociation} />
                ) : null}
              </DragOverlay>
            </Box>
          </Box>
          {/* <Box style={{ display: "flex", gap: "1rem", width: "100%" }}>

              </Box> */}
          {/* <Box style={{ display: "flex", gap: "1rem", width: "100%" }}>
                <DroppableList associations={partiesCombinedState.allParties} title="Available Parties" droppableContainerId={allPartiesContainerId} />
                <DroppableList associations={partiesCombinedState.deliveryParties} title="Deliver to" droppableContainerId={deliveryPartiesContainerId} />
                <DroppableList associations={partiesCombinedState.documentParties} title="Parties in Document" droppableContainerId={documentPartiesContainerId} />
                <DroppableList associations={partiesCombinedState.signerParties} title="Document Signers" droppableContainerId={signerPartiesContainerId} />
                <DragOverlay adjustScale={false} style={{ maxWidth: "500px", maxHeight: "100px" }}>
                  {activeAssociation ? (
                    <AssociationDroppableItem association={activeAssociation} />
                  ) : null}
                </DragOverlay>
              </Box> */}
        </DndContext>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <ContainedButton
          text={t('common:close')}
          onClick={onClose}
          disabled={isSavingLoading}
        />
        <ContainedButton
          text={t('common:save')}
          isLoading={isSavingLoading}
          onClick={handleSave}
          disabled={isSavingLoading}
        />
      </DialogActions>
    </Dialog>
  );
};
